<template>
  <a-modal
    :visible="visible"
    title="诊后病程服务费"
    :maskClosable="false"
    @cancel="handleCancel"
    @ok="handleOk"
    :footer="null"
    width="640px"
  >
    <div class="service-fee-modal">
      <div class="service-fee-total" v-if="selectedServicePackage">
        <span class="label">总费用：</span>
        <span class="value">{{ totalPrice }} 元</span>
      </div>
      <div v-else class="service-fee-empty">暂无服务包</div>

      <div v-if="selectedServicePackage" class="service-fee-section">
        <div class="service-fee-section__title">服务内容</div>
        <div class="service-fee-item" v-for="item in sortedServiceItems" :key="item.id">
          <div class="service-fee-item__title">
            <span class="service-fee-item__name">{{ item._index }}.{{ item.name }}</span>
          </div>
          <div class="service-fee-item__desc">
            {{ item.instructions || '--' }}
            <template v-if="item.equityType === 1">
              次数：
              <span class="service-fee-item__times">
                {{ item.equityTypeValue === -1 ? '无限次' : `${item.equityTypeValue}次` }}
              </span>
            </template>
          </div>
        </div>
      </div>
    </div>
  </a-modal>
</template>

<script setup>
import { defineProps, defineEmits, computed, watch, ref } from 'vue'
import { queryCourseDiseaseServiceFee } from '@/api/HealthRecord'

// 组件入参
const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  ucDoctorId: {
    type: [String, Number],
    default: null
  },
  servicePackageCode: {
    type: String,
    default: ''
  },
  policyFee: {
    type: [String, Number],
    default: null
  },
  postServiceFee: {
    type: [String, Number],
    default: null
  },
  eggGoldAmount: {
    type: [String, Number],
    default: null
  },
  equityInfoList: {
    type: Array,
    default: () => []
  }
})

// 组件事件
const emit = defineEmits(['cancel', 'ok', 'update:visible'])
// 服务费接口完整返回
const serviceFeeDetail = ref(null)
// 匹配 servicePackageCode 的服务包
const selectedServicePackage = ref(null)
// 服务包内容列表（排序+序号）
const sortedServiceItems = computed(() => {
  const list = selectedServicePackage.value?.packageServiceInfoList || []
  return [...list]
    .sort((a, b) => (a.sortNumber || 0) - (b.sortNumber || 0))
    .map((item, index) => ({
      ...item,
      _index: index + 1
    }))
})

const totalPrice = computed(() => {
  return selectedServicePackage.value.price + props.eggGoldAmount ?? 0
})

// 取消关闭
const handleCancel = () => {
  emit('cancel')
  emit('update:visible', false)
}

// 确认关闭
const handleOk = () => {
  emit('ok')
  emit('update:visible', false)
}

// 请求服务费包数据
const fetchServiceFeeDetail = async () => {
  try {
    const response = await queryCourseDiseaseServiceFee({
      ucDoctorId: props.ucDoctorId || null,
      policyFee: props.policyFee || 0,
      servicePackageCode: props.servicePackageCode
    })
    serviceFeeDetail.value = response
    selectedServicePackage.value = Array.isArray(response)
      ? response.find(item => item?.servicePackageCode === props.servicePackageCode) || null
      : null
  } catch (error) {
    serviceFeeDetail.value = null
    selectedServicePackage.value = null
  }
}

watch(
  () => props.visible,
  async visible => {
    if (!visible) return
    if (props.postServiceFee && props.equityInfoList.length) {
      // 已有服务费数据，优先使用入参数据，无需请求接口
      selectedServicePackage.value = {
        price: props.postServiceFee,
        packageServiceInfoList: props.equityInfoList
      }
    } else {
      // 无服务费数据，才请求接口获取
      await fetchServiceFeeDetail()
    }
  }
)
</script>

<style scoped lang="scss">
.service-fee-modal {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.service-fee-total {
  border-radius: 8px;
  background: #f1f7ff;
  border: 1px solid #96c9fc;
  padding: 16px 14px;
  display: flex;
  font-size: 14px;
  font-weight: 500;
}

.service-fee-empty {
  color: #999;
  font-size: 14px;
}

.service-fee-section__title {
  font-weight: 500;
  font-size: 16px;
  color: #333333;
  margin-bottom: 16px;
}

.service-fee-item {
  margin-bottom: 20px;
}

.service-fee-item__title {
  font-weight: 500;
  font-size: 14px;
  color: #3b3b3b;
  margin-bottom: 8px;
}
.service-fee-item__desc {
  font-weight: 400;
  font-size: 14px;
  color: #666666;
}

.service-fee-item__times {
  color: #f84f4f;
}

.label {
  color: #999999;
}

.value {
  color: #333333;
}
</style>
